/**
 * Status groups represent logical stages of the patient/exam journey. Each WorkflowID
 * belongs to exactly one group; rendering uses the group's color, and the legend uses
 * its label.
 *
 * Colors are aligned with the SCSS variables in src/styles/variables.scss when available:
 *   $color-brand-p-1  → scheduling
 *   $color-warning    → presence
 *   $color-brand-g-4  → report processing (light teal-green)
 *   $color-brand-g-1  → report delivery (medium teal-green)
 *   $color-success    → finished (dark green)
 *   $color-error      → problematic
 *   $color-gray-3     → neutral
 * The two outliers (confirmed/blue and in-service/cyan) have no var equivalent and use
 * literal hex codes.
 */
export type StatusGroupId =
  | 'scheduling'
  | 'confirmed'
  | 'presence'
  | 'inService'
  | 'reportProcessing'
  | 'reportDelivery'
  | 'finished'
  | 'problematic'
  | 'neutral'

export interface StatusGroup {
  id: StatusGroupId
  label: string
  color: string
  /** Optional parent label used by the legend tree view (e.g. "Laudo" parent). */
  parent?: string
}

export const STATUS_GROUPS: Record<StatusGroupId, StatusGroup> = {
  scheduling:       { id: 'scheduling',       label: 'Agendamento',         color: '#5505a2' },
  confirmed:        { id: 'confirmed',        label: 'Confirmado',          color: '#3b82f6' },
  presence:         { id: 'presence',         label: 'Presença na clínica', color: '#ebbc2e' },
  inService:        { id: 'inService',        label: 'Em atendimento',      color: '#06b6d4' },
  reportProcessing: { id: 'reportProcessing', label: 'Processamento',       color: '#4de7bf', parent: 'Laudo' },
  reportDelivery:   { id: 'reportDelivery',   label: 'Entrega',             color: '#00dda3', parent: 'Laudo' },
  finished:         { id: 'finished',         label: 'Finalizado',          color: '#4ab858' },
  problematic:      { id: 'problematic',      label: 'Problemáticos',       color: '#ed3a3a' },
  neutral:          { id: 'neutral',          label: 'Neutros / Informativos', color: '#8f9596' },
}

/** Map WorkflowID → group id. The single source of truth for status grouping. */
export const WORKFLOW_STATUS_GROUP: Record<number, StatusGroupId> = {
  // Agendamento (roxo)
  1: 'scheduling',   // Agendado
  31: 'scheduling',  // Pré-agendamento
  35: 'scheduling',  // Reconvocar Exame

  // Confirmado (azul)
  2: 'confirmed',    // Confirmado
  3: 'confirmed',    // Confirmado E-mail
  4: 'confirmed',    // Confirmado via SMS

  // Presença na clínica (amarelo)
  5: 'presence',     // Sala de Espera
  50: 'presence',    // CheckIn Realizado

  // Em atendimento (ciano)
  30: 'inService',   // Em Atendimento

  // Laudo - Processamento (verde-água claro)
  7: 'reportProcessing',   // Laudando
  8: 'reportProcessing',   // Revisando
  9: 'reportProcessing',   // Revisar
  10: 'reportProcessing',  // Assinar
  20: 'reportProcessing',  // Digitando
  22: 'reportProcessing',  // Digitação
  // Note: "Sistema Digitando Laudo" tipically ID-mapped to digitando group (treated as such)

  // Laudo - Entrega (verde-água médio)
  11: 'reportDelivery',    // Envelopado
  12: 'reportDelivery',    // Entregar
  13: 'reportDelivery',    // Entregue
  14: 'reportDelivery',    // Laudo Online
  15: 'reportDelivery',    // Disponível p/ Entrega
  26: 'reportDelivery',    // E-mail Entrega de Exame
  27: 'reportDelivery',    // SMS Entrega de Exame

  // Finalizado (verde escuro)
  16: 'finished',          // Atendido
  19: 'finished',          // Atendido Sistema
  21: 'finished',          // Laudo Finalizado

  // Problemáticos (vermelho)
  17: 'problematic',       // Bloqueado
  18: 'problematic',       // Faltou
  23: 'problematic',       // Interrompido
  24: 'problematic',       // Laudo Descartado
  29: 'problematic',       // Cancelado
  32: 'problematic',       // Desmarcado
  36: 'problematic',       // Exame Sem Laudo

  // Neutros / Informativos (cinza)
  6: 'neutral',            // Observação
  25: 'neutral',           // Laudo Arquivado
  28: 'neutral',           // Transferido
  37: 'neutral',           // Laudos Migrados
}

/** Status name table — used by tooltips and the legend tree view. */
export const WORKFLOW_STATUS_NAMES: Record<number, string> = {
  1: 'Agendado',
  2: 'Confirmado',
  3: 'Confirmado E-mail',
  4: 'Confirmado via SMS',
  5: 'Sala de Espera',
  6: 'Observação',
  7: 'Laudando',
  8: 'Revisando',
  9: 'Revisar',
  10: 'Assinar',
  11: 'Envelopado',
  12: 'Entregar',
  13: 'Entregue',
  14: 'Laudo Online',
  15: 'Disponível p/ Entrega',
  16: 'Atendido',
  17: 'Bloqueado',
  18: 'Faltou',
  19: 'Atendido Sistema',
  20: 'Digitando',
  21: 'Laudo Finalizado',
  22: 'Digitação',
  23: 'Interrompido',
  24: 'Laudo Descartado',
  25: 'Laudo Arquivado',
  26: 'E-mail Entrega de Exame',
  27: 'SMS Entrega de Exame',
  28: 'Transferido',
  29: 'Cancelado',
  30: 'Em Atendimento',
  31: 'Pré-agendamento',
  32: 'Desmarcado',
  35: 'Reconvocar Exame',
  36: 'Exame Sem Laudo',
  37: 'Laudos Migrados',
  50: 'CheckIn Realizado',
}

/** Default group used when an unknown WorkflowID shows up (defensive fallback). */
const DEFAULT_GROUP: StatusGroupId = 'neutral'

/** Resolves a WorkflowID to its group descriptor. */
export function getStatusGroup(workflowId: number | undefined): StatusGroup {
  const groupId = WORKFLOW_STATUS_GROUP[workflowId ?? 0] ?? DEFAULT_GROUP
  return STATUS_GROUPS[groupId]
}

/** Resolves a WorkflowID to its hex color (kept for backward compat with existing call sites). */
export function getStatusColor(workflowId: number | undefined): string {
  return getStatusGroup(workflowId).color
}

/**
 * Backward compat alias: previous flat WorkflowID → color map. Now derived from groups so
 * it stays in sync automatically. Kept as a named export in case old code imported it.
 */
export const WORKFLOW_STATUS_COLORS: Record<number, string> = Object.fromEntries(
  Object.entries(WORKFLOW_STATUS_GROUP).map(([id, gid]) => [Number(id), STATUS_GROUPS[gid].color]),
)

/**
 * Schedule resource for FullCalendar columns
 */
export interface AgendaSchedule {
  id: number
  name: string
  professional?: string
  doctorId?: number
  color?: string
  scheduled?: number
  free?: number
  generatePartialFreeSlots?: boolean
  minMinutesForFreeSlot?: number
}

/**
 * Base shape for all calendar slots (shared fields)
 */
export interface CalendarSlotBase {
  scheduleId: number
  start: string // ISO datetime
  end: string // ISO datetime
  /** Users that locked this slot. Empty when not locked. */
  lockUsers: { id: number; name: string }[]
}

/**
 * Free slot — available time for scheduling
 */
export interface FreeSlot extends CalendarSlotBase {
  type: 'free'
  /** Original grid slot boundaries (when this free slot is a partial fragment). */
  originalStart?: string
  originalEnd?: string
  observation?: string
  services?: { id: number; name: string }[]
}

/**
 * Appointment slot — occupied time with patient data
 */
export interface AppointmentSlot extends CalendarSlotBase {
  type: 'appointment'
  id: number | string
  isFitIn: boolean
  observation?: string
  status?: { id: number; name: string }
  patient?: { id: number; name: string; birthDate?: string; phone?: string }
  insurance?: { id: number; name: string }
  procedures?: { id: number; name: string }[]
  performingDoctor?: { id: number; name: string }
  requestingDoctor?: { id: number; name: string }
  /** Whether the appointment insurance is chargeable at the cashier. */
  insuranceChargeable?: boolean
  /** Payment status: 'unpaid', 'partial' or 'paid'. */
  paymentStatus?: 'unpaid' | 'partial' | 'paid'
  /** Origin of the appointment: 'package' when it comes from a package, undefined otherwise. */
  origin?: 'package'
  /** Whether the procedure requires a medical report (laudo). */
  requiresMedicalReport?: boolean
  /** ID do slot da grade. Quando ausente, o agendamento é um encaixe (fit-in). */
  slotId?: number
  extendedProps?: Record<string, unknown>
}

/** Whether the appointment is a fit-in (not tied to a grid slot). */
export function isAppointmentFitIn(appointment: AppointmentSlot): boolean {
  return appointment.isFitIn
}

/**
 * Blocked slot — time blocked for a specific reason (no scheduling allowed)
 */
export interface BlockedSlot extends CalendarSlotBase {
  type: 'blocked'
  observation?: string
}

/**
 * Unified calendar slot — free, appointment, or blocked
 */
export type CalendarSlot = FreeSlot | AppointmentSlot | BlockedSlot

/**
 * Backend response for GET /time-slot/content (ScheduleGridEntity[])
 */
export interface ScheduleGridResponse {
  scheduleId: number
  scheduleName: string
  days: ScheduleGridDayResponse[]
}

export interface ScheduleGridDayResponse {
  date: string
  slots: ScheduleGridSlotResponse[]
}

export interface ScheduleGridSlotResponse {
  startDateTime: string
  endDateTime: string
  type: 'free' | 'appointment' | 'blocked'
  slotId?: number
  templateObservation?: string
  templateServices?: { id: number; name: string }[]
  appointment?: ScheduleGridSlotAppointmentResponse
  block?: ScheduleGridSlotBlockResponse
  lock?: ScheduleGridSlotLockResponse
  occupiedSlots?: { slotId: number; startDateTime: string; endDateTime: string }[]
}

export interface ScheduleGridSlotAppointmentResponse {
  id: number
  isExtraSlot: boolean
  observation?: string
  status: { id: number; name: string }
  patient?: { id: number; name: string; birthDate?: string; phone?: string }
  insurance?: { id: number; name: string }
  procedures: { id: number; name: string }[]
  requestingDoctor?: { id: number; name: string }
  performingDoctor?: { id: number; name: string }
}

export interface ScheduleGridSlotBlockResponse {
  id: number
  observation?: string
}

export interface ScheduleGridSlotLockResponse {
  users: { id: number; name: string }[]
}

/**
 * Date range for the calendar view
 */
export interface AgendaDateRange {
  start: string // ISO date
  end: string // ISO date
  specificDates?: string[] // ISO dates for 'multiple' selection mode
}

/**
 * Event emitted when user clicks on an event or slot
 */
export interface AgendaEventClick {
  type: 'appointment' | 'free' | 'blocked'
  data: CalendarSlot
}

/**
 * Event emitted when user moves an appointment to a different schedule/time
 */
export interface AgendaEventMove {
  appointment: AppointmentSlot
  oldScheduleId: number
  newScheduleId: number
  oldStart: string
  oldEnd: string
  newStart: string
  newEnd: string
  isFitIn?: boolean
}

export interface AgendaFitSlot {
  scheduleId: number
  start: string
  end: string
}

/**
 * Socket event received when another client creates an appointment.
 */
export interface AppointmentCreatedEvent {
  scheduleId: number
  appointmentId: number
  date: string
  startTime: string
}

/**
 * Socket event received when another client cancels an appointment.
 */
export interface AppointmentCancelledEvent {
  scheduleId: number
  appointmentId: number
  date: string
  startTime: string
}

/**
 * Socket event received when blocked slots are updated.
 */
export interface BlockedSlotsUpdatedEvent {
  id: number
  scheduleId: number
  startDate: string
  endDate: string
  observation: string | null
  appointmentId: number | null
}

/**
 * Socket event received when another client transfers an appointment.
 * Matches the backend AppointmentTransferredPayload shape.
 */
export interface AppointmentTransferredEvent {
  scheduleIds: number[]
  schedulings: Array<{
    appointmentId: number
    sourceScheduleId: number
    destinationScheduleId: number
    date: string
    startTime: string
  }>
}

/**
 * Grade de horários de uma agenda (vinda do backend).
 * Define os intervalos de tempo disponíveis para agendamento.
 */
export interface ScheduleTimeGrid {
  scheduleId: number
  slots: { start: string; end: string; observation?: string; services?: { id: number; name: string }[] }[]
  generatePartialFreeSlots?: boolean
  minMinutesForFreeSlot?: number
}

/**
 * Snapshot of calendar data passed to service methods.
 */
export interface CalendarContext {
  slots: CalendarSlot[]
  grids: ScheduleTimeGrid[]
  blockedSlots: BlockedSlot[]
}

/**
 * Display configuration for the calendar.
 */
export interface CalendarDisplayConfig {
  locale: 'pt-br' | 'en' | 'es'
  slotDuration: string
  draggableStatusIds?: number[]
  dateRange?: AgendaDateRange
}

/**
 * Intermediate context gathered before deciding the movement scenario.
 */
export interface MovementContext {
  appointmentMinutes: number
  appointmentDurationMs: number
  slotsAtTarget: CalendarSlot[]
  freeSlots: FreeSlot[]
  target: CalendarSlotBase
  rawTarget: CalendarSlotBase
  movingAppointment: AppointmentSlot
  hasAppointmentCollision: boolean
}

/**
 * Result of evaluating a movement operation.
 *
 * Scenarios (escaláveis — cada novo cenário é adicionado aqui e tratado no switch do componente):
 * - blocked:          colide com outro agendamento → reverte
 * - exact:            cabe perfeitamente em um slot livre → emite direto
 * - slot-bigger:      slot livre maior que o agendamento → expande para preencher o slot inteiro
 * - outside-slot:     não encaixa em nenhum slot livre → pergunta ao usuário (encaixe)
 * - mixed-partial:    rawTarget sobrepõe agendamento(s) + slot(s) livre(s) mas o espaço livre é insuficiente
 */
export interface MovementEvaluationResult {
  scenario: 'blocked' | 'exact' | 'slot-bigger' | 'outside-slot' | 'mixed-partial'
  remainingSlots: AgendaFitSlot[]
  coveredMinutes: number
  totalMinutes: number
  containingSlot?: FreeSlot
  /** Slot livre disponível no destino (para resize quando appointment é maior que o slot livre) */
  availableFreeSlot?: CalendarSlotBase
  /** Slot livre próximo que comporta o agendamento completo (apenas para mixed-partial) */
  nearbyAlternativeSlot?: CalendarSlotBase
}

/**
 * Result of a moveSlot operation.
 *
 * - blocked: movement is not possible
 * - exact / slot-bigger: movement applied automatically (moveData is ready)
 * - needs-confirmation: user must confirm (outside-slot or mixed-partial)
 */
export type MoveSlotResult =
  | { action: 'blocked'; error: string }
  | { action: 'moved'; moveData: AgendaEventMove }
  | {
      action: 'needs-confirmation'
      moveData: AgendaEventMove
      coveredMinutes: number
      totalMinutes: number
      availableFreeSlot?: CalendarSlotBase
      canKeepOriginalDuration: boolean
      nearbyAlternativeSlot?: CalendarSlotBase
    }
